import {
  Alert,
  Box,
  Button,
  Container,
  LoadingOverlay,
  Paper,
  Text,
  ThemeIcon,
  Title
} from '@mantine/core'
import { IconCheck, IconKey, IconMailbox, IconShieldLock, IconX } from '@tabler/icons'
import { useRouter } from 'next/router'
import { useState } from 'react'

import { useFetch } from '@/lib/hooks'
import { api, notificationHandler } from '@/lib/utils'
import { useAuth } from '@/providers/AuthProvider'
import { UserPermissions as UserPermissionsType } from '@/types'

interface Props {
  permissions?: UserPermissionsType
}

export default function ProtectedRequest({ permissions }: Props) {
  // Hooks
  const router = useRouter()
  const { isAuthenticated } = useAuth() || {}
  const { site: siteSlug } = router?.query || {}

  // Fetch
  const { data, error, mutate } = useFetch(
    [isAuthenticated ? `/${siteSlug}/accounts/permissions/` : null],
    { fallbackData: permissions }
  )

  // Constants
  const { site } = data || {}
  const isPending = site?.status === 'pending'

  // States
  const [isLoading, setIsLoading] = useState(false)

  // Actions
  const handleRquest = async () => {
    setIsLoading(true)
    try {
      await api.post(`/${siteSlug}/request-access/`)
      mutate()
      notificationHandler({
        variant: 'success',
        message: 'A sua solicitação foi enviada e em breve será respondida!',
        icon: <IconCheck />
      })
    } catch (error) {
      notificationHandler({
        variant: 'error',
        message: 'Houve um erro ao enviar sua solicitação, tente novamente mais tarde.',
        icon: <IconX />
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Container p="xl" size="sm">
      <LoadingOverlay visible={!data && !error} />
      <Box hidden={!data && !error}>
        <Box sx={{ display: 'flex', alignItems: 'center' }} mb="xl">
          <ThemeIcon variant="outline" size={48} radius="md" mr="sm">
            <IconShieldLock size={32} />
          </ThemeIcon>
          <Title order={3}>Site protegido</Title>
        </Box>
        <Paper p="lg" withBorder mb="xl">
          <Text mb="xs">
            Esta é uma mensagem de boas vindas exibida para usuários <em>autenticados</em> em sites
            com privacidade <strong>Protegida</strong>.
          </Text>
          <Text mb="md" hidden={isPending}>
            Para que você possa acessar este canal será necessário solicitar o acesso.
          </Text>
          <Button
            size="md"
            fullWidth
            leftIcon={<IconKey />}
            onClick={handleRquest}
            hidden={isPending}
            loading={isLoading}
            disabled={isLoading}>
            Solicitar acesso
          </Button>
          <Alert
            icon={<IconMailbox />}
            title="Aguardando aprovação"
            color="blue"
            hidden={!isPending}>
            <Text color="blue">
              A sua solicitação foi enviada. Assim que for aprovada, o seu acesso será concedido.
            </Text>
          </Alert>
        </Paper>
      </Box>
    </Container>
  )
}
